<script setup>
import { ref, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import tramiteFormResponses from 'dashboard/api/tramiteFormResponses';
import { normalizeFormField } from 'forms/utils/normalizeFormField';
import { isFieldVisible, isFieldRequired } from 'forms/utils/conditions';
import FormField from 'forms/components/FormField.vue';
import FormFieldGroup from 'forms/components/FormFieldGroup.vue';

const props = defineProps({
  tramiteId: {
    type: Number,
    required: true,
  },
  responseId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['submitted']);

const { t } = useI18n();

const dialogRef = ref(null);
const isLoading = ref(false);
const isSubmitting = ref(false);

const fields = ref([]);
const values = reactive({});
const errors = reactive({});

const resetState = () => {
  fields.value = [];
  Object.keys(values).forEach(k => delete values[k]);
  Object.keys(errors).forEach(k => delete errors[k]);
};

const loadResponse = async () => {
  if (!props.responseId) return;
  isLoading.value = true;
  try {
    const { data } = await tramiteFormResponses.show(props.tramiteId, props.responseId);
    const pages = data?.form_options?.pages || [];
    const rawFields = pages.flatMap(p => p.fields || []);
    fields.value = rawFields
      .map(normalizeFormField)
      .filter(f => f.type !== 'documents');
  } catch {
    useAlert(t('TRAMITES.FORMS.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const validate = () => {
  Object.keys(errors).forEach(k => delete errors[k]);
  let valid = true;
  for (const field of fields.value) {
    if (!isFieldVisible(field, values)) continue;
    if (isFieldRequired(field, values) && !values[field.name]) {
      errors[field.name] = t('TRAMITES.FORMS.REQUIRED_ERROR');
      valid = false;
    }
  }
  return valid;
};

const onSubmit = async () => {
  if (!validate()) return;
  isSubmitting.value = true;
  try {
    await tramiteFormResponses.submit(props.tramiteId, props.responseId, { ...values });
    useAlert(t('TRAMITES.FORMS.SUBMIT_SUCCESS'));
    emit('submitted');
    close();
  } catch {
    useAlert(t('TRAMITES.FORMS.SUBMIT_ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const open = async () => {
  resetState();
  dialogRef.value?.open();
  await loadResponse();
};

const close = () => dialogRef.value?.close();

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t('TRAMITES.FORMS.TITLE')"
    :confirm-button-label="t('TRAMITES.FORMS.SAVE')"
    :is-loading="isSubmitting"
    @confirm="onSubmit"
  >
    <div v-if="isLoading" class="flex justify-center py-6">
      <span class="i-lucide-loader-circle animate-spin size-5 text-n-slate-10" />
    </div>

    <div v-else class="flex flex-col gap-4 max-h-[60vh] overflow-y-auto px-1">
      <template v-for="field in fields" :key="field.name">
        <template v-if="isFieldVisible(field, values)">
          <FormFieldGroup
            v-if="field.type === 'group'"
            :field="field"
            :values="values"
            :errors="errors"
            @update="(name, val) => { values[name] = val; }"
          />
          <FormField
            v-else
            :field="field"
            :model-value="values[field.name]"
            :error="errors[field.name]"
            @update:model-value="values[field.name] = $event"
          />
        </template>
      </template>
    </div>
  </Dialog>
</template>
